import type { Card } from "@amzn/ads-blink-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import { useStore } from "zustand";

import { ContentSearchErrorNotificationMessage } from "../../components/common/notification.components";
import { notificationStore, NotificationType } from "../../store/notification";
import { unifiedFilterStore } from "../../store/unifiedFilters";
import { isSuccess } from "../../utils/api.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";

import { useSearchInsightFamilyTypes } from "./useSearchInsightFamilyTypes";

export function useInsightFamiliesLoader(): [AsyncStatus, Card[], number] {
    const [contents, setContents] = useState<Card[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [
        filterContentStateIsDirty,
        setIsContentDirty,
        setIsMetadataDirty,
        setLastSearchParameters,
        setLastSearchResultTotalCount,
    ] = useStore(unifiedFilterStore, (state) => [
        state.isContentDirty,
        state.setIsContentDirty,
        state.setIsMetadataDirty,
        state.setLastSearchParameters,
        state.setLastSearchResultTotalCount,
    ]);

    const searchId = useRef<string>(uuid.v4());
    const searchErrors = useRef<string[]>([]);
    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );

    const searchAdvertiserId = isAccountAgnosticId()
        ? undefined
        : getAdvertiserId()!;
    const marketplaceId = getMarketplaceId()!;

    const [searchState, searchAction] = useSearchInsightFamilyTypes({
        advertiserId: searchAdvertiserId,
        marketplaceId,
        autoFetch: false,
    });

    const contentRequestID = useRef(uuid.v4());

    const fetchContents = useCallback(async () => {
        const newId = uuid.v4();
        contentRequestID.current = newId;

        setContents([]);
        setTotalCount(0);

        // Set last search parameters before API call
        setLastSearchParameters();

        searchAction
            .execute()
            .then(({ content, totalHits }) => {
                if (contentRequestID.current !== newId) return;

                // Update contents
                setContents(content);
                // Update total count
                setTotalCount(totalHits);
                setLastSearchResultTotalCount(totalHits);
            })
            .catch((error) => {
                // Check if request is still valid
                if (contentRequestID.current !== newId) return;

                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : "Unknown error occurred";
                searchErrors.current = [...searchErrors.current, errorMessage];
            });
    }, [
        searchAction,
        searchState.result,
        setLastSearchParameters,
        setLastSearchResultTotalCount,
    ]);

    /**
     * Update the error message shown to the user when we get a new error
     */
    useEffect(() => {
        if (searchErrors.current.length) {
            upsertNotification(
                <ContentSearchErrorNotificationMessage
                    errors={searchErrors.current}
                />,
                NotificationType.ERROR,
                searchId.current,
            );
        } else {
            dismissNotification(searchId.current);
            searchId.current = uuid.v4();
        }
    }, [searchErrors.current, upsertNotification, dismissNotification]);

    // When we load this component, dirty our filter state
    useEffect(() => {
        setIsContentDirty(true);
        setIsMetadataDirty(true);
    }, [setIsContentDirty, setIsMetadataDirty]);

    /**
     * Trigger a refresh of the insights after 200ms of inactivity when:
     * 1. searchQuery - The user has changed the term they're searching for
     * 2. fieldSelections - The user has changed the filters they want to apply
     * 3. contentSubType - The user selected a new insight type
     */
    useDebouncedEffect(
        () => {
            const hasChanges = filterContentStateIsDirty;
            if (hasChanges) {
                searchErrors.current = [];
                void fetchContents().then(() => setIsContentDirty(false));
                setIsMetadataDirty(true);
            }
        },
        [
            filterContentStateIsDirty,
            fetchContents,
            setIsContentDirty,
            setIsMetadataDirty,
        ],
        200,
    );

    /**
     * Log data each time we retrieve insights
     */
    useDebouncedEffect(
        () => {
            if (isSuccess(searchState.status)) {
                logInsightBrowseResults(contents.length);
            }
        },
        [contents, searchState.status],
        200,
    );

    return [searchState.status, contents, totalCount];
}
